import React, { useEffect, useMemo, useState } from 'react';
import DatePicker from '../../components/DatePicker';
import api from '../../services/api';
import SelectDropdown from '../../components/SelectDropdown';
import { useDispatch, useSelector } from 'react-redux';
import { TransformDate } from '../../components/DatePicker/DatePickerUtils/datePickerUtils';
import { paidSearchActions } from '../../modules/reducer/paidSearchSlice';
import selectors from '../../modules/selectors/datePickerSelectors';
import { paidSearchPayload, updateFiltersInPayload } from './query';
import { useMutation } from '@tanstack/react-query';
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import NoData from '../../components/NoData';
import { psColumns } from '../../data/paidSearchData';
import DownloadButton from '../../components/DownloadReport';

const PaidSearch = (props) => {
  const { userData } = useSelector((state) => state.auth);
  const options = useMemo(() => {
    const stores = userData.data.stores_data.map((store) => ({
      label: store.branch_name,
      value: store.branch_number,
      ...store,
    }));
    const GaleMarkets = stores.filter((store) => store.value === 1);
    if (GaleMarkets?.length) {
      return [
        GaleMarkets[0],
        ...stores
          .filter((store) => store.value !== 1)
          .sort((a, b) => a.label.localeCompare(b.label)),
      ];
    } else {
      return [
        ...stores
          .filter((store) => store.value !== 1)
          .sort((a, b) => a.label.localeCompare(b.label)),
      ];
    }
  }, [userData]);
  const [storeId, setStoreId] = useState(options[0].value);
  const paidSearchTableId = userData?.data?.data_sources?.find(
    (data) => data?.name === 'paid_search'
  )?.identifier;
  const { paidSearchRange } = useSelector(selectors.paidSearchCombinedData);
  const { transformedData, transformedDataWithDate } = useSelector(
    selectors.datePickerCombinedData
  );
  const [defaultFilter, setDefaultFilter] = useState('TIME PERIOD');
  const [currentFilterVal, setCurrentFilterVal] = useState([]);
  const [componentType, setComponentType] = useState('PAID SEARCH');
  const [paidSearchData, setPaidSearchData] = useState([]);
  const storeArray = userData?.data?.stores_data?.map((store) => store.branch_number);
  const [storeList, setStoreList] = useState(storeArray);
  const [isDownload, setIsDownload] = useState(false);
  const [downloadReportData, setDownloadReportData] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const dispatch = useDispatch();

  const checkForGlobalObject = (object) => {
    if (object === null || object === undefined) {
      return false; // Return false if object is null or undefined
    }
    return (
      Object.values(object).every((value) => value === null || value === undefined) ||
      Object.values(object).some((value) => value === null || value === undefined)
    );
  };

  useEffect(() => {
    timePeriodRangeHandler();
  }, [transformedData]);

  useEffect(() => {
    let storeIdFilter = {
      col: 'store_id',
      op: 'in',
      val: isDownload ? storeArray : [storeId],
    };

    if (!checkForGlobalObject(paidSearchRange)) {
      setCurrentFilterVal([
        {
          col: 'dateindex',
          op: '>=',
          val: paidSearchRange?.start_year
            ?.toString()
            ?.concat(
              paidSearchRange?.start_week <= 9 && paidSearchRange?.start_week > 0
                ? '0'.concat(paidSearchRange?.start_week)
                : paidSearchRange?.start_week
            ),
        },
        {
          col: 'dateindex',
          op: '<=',
          val: paidSearchRange?.end_year
            ?.toString()
            ?.concat(
              paidSearchRange?.end_week <= 9 && paidSearchRange?.end_week > 0
                ? '0'.concat(paidSearchRange?.end_week)
                : paidSearchRange?.end_week
            ),
        },
        storeIdFilter,
      ]);
    }
  }, [paidSearchRange, storeId, isDownload]);

  const handleStoreIdChange = (store) => {
    setStoreId(store);
  };

  const timePeriodRangeHandler = () => {
    const allValuesAreNull = checkForGlobalObject(paidSearchRange);
    const obj = TransformDate?.getCurrentYearAndQuarter(transformedDataWithDate);
    if (allValuesAreNull && obj && Object.keys(obj).length) {
      const currYear = obj.quarter === 'Q1' ? obj.year - 1 : obj.year;
      const quarterValue =
        transformedData.length &&
        TransformDate.quickFilters.LAST_QUARTER(transformedData, currYear);
      dispatch(paidSearchActions.setPaidSearchRange(quarterValue));
      const value =
        paidSearchRange &&
        `w${paidSearchRange?.start_week}, ${paidSearchRange?.start_year} - w${paidSearchRange?.end_week}, ${paidSearchRange?.end_year}`;
      setDefaultFilter(value);
    }
  };

  const quarterAndYearValueHandler = () => {
    const lastQuarterValue = TransformDate.getLastQuarter();
    dispatch(paidSearchActions.setCurrentQuarter(lastQuarterValue));
    const obj = TransformDate?.getCurrentYearAndQuarter(transformedDataWithDate);
    if (obj && Object.keys(obj).length) {
      dispatch(paidSearchActions.setCurrentYear(obj.year));
    }
  };

  useEffect(() => {
    quarterAndYearValueHandler();
  }, [transformedDataWithDate]);

  const fetchPaidSearchDataCurrent = async () => {
    try {
      const result = await api.post(
        '/api/v1/query',
        updateFiltersInPayload(paidSearchPayload, currentFilterVal, paidSearchTableId)
      );
      return result?.data;
    } catch (error) {
      console.error(error);
    }
  };

  const {
    data: paidSearchCurrentData,
    mutate: paidSearchCurrentMutation,
    isLoading: isLoadingPaidSearchCurrentData,
  } = useMutation({
    mutationFn: fetchPaidSearchDataCurrent,
  });

  useEffect(() => {
    if (storeId && currentFilterVal?.length > 0 && paidSearchTableId) {
      paidSearchCurrentMutation({ currentFilterVal, paidSearchTableId });
    }
  }, [storeId, currentFilterVal, paidSearchTableId, isDownload]);

  useEffect(() => {
    if (paidSearchCurrentData && Object.keys(paidSearchCurrentData).length > 0) {
      setPaidSearchData(paidSearchCurrentData?.data?.result[0]?.data);
    }
  }, [paidSearchCurrentData, paidSearchData]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  useEffect(() => {
    if (paidSearchData?.length > 0) {
      const result = (isDownload ? storeArray : [storeId])?.map((store) => {
        return {
          store_id: store,
          rows: paidSearchData?.filter((row) => row?.store_id === store),
          columns: psColumns?.map((col) => ({ Header: col?.header, accessor: col.accessor })),
        };
      });
      setDownloadReportData(result);
      setIsDownload(false);
    }
  }, [paidSearchData, storeId]);

  return (
    <div style={{ boxSizing: 'border-box', height: '88vh', overflow: 'scroll', padding: '20px' }}>
      <div style={{ width: 'fitContent', marginBottom: '55px' }}>
        <div
          style={{
            width: '90%',
            textAlign: 'left',
            margin: 'auto',
            fontSize: '1.5rem',
            fontWeight: '500',
            lineHeight: '1.6',
          }}
        >
          Paid Search Performance
          {paidSearchRange &&
            ` [W${paidSearchRange.start_week}, ${paidSearchRange.start_year} - W${paidSearchRange.end_week}, ${paidSearchRange.end_year}]`}
        </div>
      </div>
      <div
        style={{
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          gap: '13px',
          width: '90%',
          margin: 'auto',
        }}
      >
        <div style={{ marginRight: 'auto' }}>
          <SelectDropdown
            options={options}
            label={'Select Branch'}
            singleSelectDefaultValue={options[0].value}
            width={'200px'}
            handleFilterChange={handleStoreIdChange}
            marginExist={false}
          />
        </div>
        <DatePicker setFilter={defaultFilter} component={componentType} combineCompare={false} />
        <DownloadButton
          data={downloadReportData}
          downloadCheck={
            storeList?.length === 1
              ? downloadReportData?.length === storeList?.length && isDownload
              : downloadReportData?.length === storeList?.length
          }
          filename="paid_search.xlsx"
          setIsDownload={setIsDownload}
          setIsDownloading={setIsDownloading}
          tab={'paid_search'}
        />
      </div>
      <div style={{ width: '90%', margin: 'auto' }}>
        {isLoadingPaidSearchCurrentData ||
        downloadReportData?.length === 0 ||
        !downloadReportData?.some((store) => store.store_id === storeId) ||
        isDownload ||
        isDownloading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : paidSearchData?.length ? (
          <Paper sx={{ margin: '30px auto', width: '100%', overflow: 'hidden' }}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    {psColumns?.map((column) => (
                      <TableCell
                        style={{
                          fontWeight: 600,
                          color: '#fff',
                          background: '#222',
                          padding: '14px',
                        }}
                        key={column.header}
                      >
                        {column.header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paidSearchData?.map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {psColumns.map((column, columnIndex) => (
                        <TableCell
                          key={column.header}
                          style={columnIndex === 0 ? { fontWeight: 'bold' } : {}}
                        >
                          {column.transform
                            ? column.transform(row[column?.accessor])
                            : column.unit === '$'
                              ? formatCurrency(row[column?.accessor])
                              : column.unit === '%'
                                ? `${row[column.accessor]?.toFixed(2)}%`
                                : column.unit === 'trunc'
                                  ? `${Math.trunc(row[column?.accessor])}`
                                  : row[column?.accessor]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <NoData />
          </Box>
        )}
      </div>
    </div>
  );
};

export default PaidSearch;
